import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    Box, Switch, TextField, FormControlLabel,
    FormControl, InputLabel, OutlinedInput, InputAdornment, IconButton, FormHelperText,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { updateById } from '../../../../services/usersService';
import { useToaster } from '../../../../providers/ToasterProvider';
import RadiusButton from '../../../../components/RadiusButton';
import patterns from '../../../../constant/patterns';

import EYE_ICON from '../../../../assets/images/eye.svg';
import EYE_CLOSE_ICON from '../../../../assets/images/eye-close.svg';
import BACK from '../../../../assets/images/back.svg';

// ─── Schema ───────────────────────────────────────────────────────────────────

const profileSchema = z.object({
    firstName: z.string().min(1, { message: 'First name is required.' }),
    lastName: z.string().min(1, { message: 'Last name is required.' }),
    password: z.string().optional(),
}).superRefine(({ password }, ctx) => {
    if (password && password.length < 8) {
        ctx.addIssue({ code: 'custom', message: 'Password should be at least 8 characters long', path: ['password'] });
    }
    if (password && !patterns.PASSWORD.test(password)) {
        ctx.addIssue({ code: 'custom', message: 'Password must contain one uppercase, one lowercase, one digit and one special character', path: ['password'] });
    }
});

// ─── Component ────────────────────────────────────────────────────────────────

export const EvaluatorProfilePage = ({
    userDetails,
    setUserDetails,
    isActive,
    setIsActive,
    mailModal,
    setMailModal,
    onGoBack,
}) => {
    const { addNotification } = useToaster();
    const [isLoading, setIsLoading] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        setValue,
    } = useForm({ resolver: zodResolver(profileSchema) });

    // Pre-fill form whenever userDetails arrives / changes
    useEffect(() => {
        if (userDetails) {
            setValue('firstName', userDetails.firstName);
            setValue('lastName', userDetails.lastName);
        }
    }, [userDetails, setValue]);

    const onSubmit = async (values) => {
        setIsLoading(true);
        try {
            const body = { firstName: values.firstName, lastName: values.lastName, isActive };
            if (values.password) body.password = values.password;
            const res = await updateById(userDetails.id, body);
            if (res.code === 'OK') {
                addNotification('Profile updated successfully', 'success');
                if (values.password) {
                    setUserDetails({ ...res.data.user, password: values.password });
                    setMailModal({ ...mailModal, isUpdated: true, isOpen: true });
                }
            }
        } catch (err) {
            console.log('Error updating profile', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ p: 4 }} className='create-user-form-wrapper'>
            <Box component='form' autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
                <div className='user-form-details'>

                    <TextField id='pfFirstName' className='form-field'
                        label={<><Box component='span'>First Name</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                        variant='outlined' {...register('firstName')}
                        error={Boolean(errors.firstName)} helperText={errors.firstName?.message}
                        onBlur={() => trigger('firstName')} InputLabelProps={{ shrink: true }} />

                    <TextField id='pfLastName' className='form-field'
                        label={<><Box component='span'>Last Name</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                        variant='outlined' {...register('lastName')}
                        error={Boolean(errors.lastName)} helperText={errors.lastName?.message}
                        onBlur={() => trigger('lastName')} InputLabelProps={{ shrink: true }} />

                    {/* Read-only fields */}
                    <TextField id='pfUsername' className='form-field' label='User Name'
                        variant='outlined' value={userDetails?.username ?? ''} disabled
                        InputLabelProps={{ shrink: true }} />

                    <TextField id='pfEmail' className='form-field' label='Email'
                        variant='outlined' value={userDetails?.email ?? ''} disabled
                        InputLabelProps={{ shrink: true }} />

                    {/* Optional password change */}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControl variant='outlined' sx={{ mb: 1 }}>
                            <InputLabel htmlFor='pwEdit'>Password</InputLabel>
                            <OutlinedInput id='pwEdit' type={passwordVisible ? 'text' : 'password'}
                                endAdornment={
                                    <InputAdornment position='end'>
                                        <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge='end'>
                                            <img className='password-eye' src={passwordVisible ? EYE_CLOSE_ICON : EYE_ICON} alt='' />
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label='Password' {...register('password')}
                                error={Boolean(errors.password)} onBlur={() => trigger('password')} />
                            {errors.password?.message && (
                                <FormHelperText sx={{ color: 'error.main' }}>{errors.password.message}</FormHelperText>
                            )}
                        </FormControl>
                        <FormHelperText sx={{ color: 'primary.main', mb: 1 }}>
                            Enter password only if you wish to update.
                        </FormHelperText>
                    </Box>

                </div>

                <FormControlLabel sx={{ mb: 3 }}
                    control={<Switch checked={isActive} onChange={() => setIsActive(!isActive)} />}
                    label={<Box component='span'>Active</Box>} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <RadiusButton sx={{ textTransform: 'uppercase', fontWeight: 500 }} color='grey'
                        variant='contained' size='large' onClick={onGoBack} startIcon={<img src={BACK} alt='' />}>
                        Back
                    </RadiusButton>
                    <LoadingButton sx={{ textTransform: 'uppercase', borderRadius: 40 }}
                        color='primary' type='submit' variant='contained' size='large' loading={isLoading}>
                        Submit
                    </LoadingButton>
                </Box>
            </Box>
        </Box>
    );
};
